import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {Slot, Slottable} from '@radix-ui/react-slot';

import {cn} from '../lib/utils';
import {Checkbox} from './checkbox';
import {IconComponent, Icons} from '../icons';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
    Icon?: React.ComponentType<{className?: string}>;
    decoration?: React.ReactNode;
    asChild?: boolean;
  }
>(
  (
    {className, inset, children, Icon, decoration, asChild = false, ...props},
    ref,
  ) => {
    const iconMarkup = Icon && <Icon className="w-4 h-4 mr-2" />;
    const decorationMarkup = decoration ?? (
      <Icons.chevronRight className="ml-auto h-4 w-4" />
    );

    const triggerClassName = cn(
      'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-card data-[state=open]:bg-card',
      inset && 'pl-8',
      className,
    );

    if (asChild) {
      return (
        <DropdownMenuPrimitive.SubTrigger
          ref={ref}
          className={triggerClassName}
          asChild
          {...props}
        >
          <Slot>
            {iconMarkup}
            <Slottable>{children}</Slottable>
            {decorationMarkup}
          </Slot>
        </DropdownMenuPrimitive.SubTrigger>
      );
    }

    return (
      <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={triggerClassName}
        {...props}
      >
        {iconMarkup}
        {children}
        {decorationMarkup}
      </DropdownMenuPrimitive.SubTrigger>
    );
  },
);
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({className, ...props}, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-32 overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    disablePortal?: boolean;
  }
>(({className, sideOffset = 4, disablePortal, ...props}, ref) => {
  const contentMarkup = (
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-32 overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  );
  if (disablePortal) {
    return contentMarkup;
  }
  return (
    <DropdownMenuPrimitive.Portal>{contentMarkup}</DropdownMenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

interface DropdownMenuListItem {
  type: 'item';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  Icon?: IconComponent;
  className?: string;
}

interface DropdownMenuListSeparator {
  type: 'separator';
}

export type DropdownMenuListEntry =
  | DropdownMenuListItem
  | DropdownMenuListSeparator;

export const DropdownMenuContentList = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  Omit<
    React.ComponentPropsWithoutRef<typeof DropdownMenuContent>,
    'children'
  > & {
    entries: DropdownMenuListEntry[];
  }
>(({className, entries, ...props}, ref) => (
  <DropdownMenuContent ref={ref} className={className} {...props}>
    {entries.map((entry, index) => (
      <React.Fragment key={index}>
        {entry.type === 'item' && (
          <DropdownMenuItem
            Icon={entry.Icon}
            onClick={entry.onClick}
            className={entry.className}
            disabled={entry.disabled}
          >
            {entry.label}
          </DropdownMenuItem>
        )}
        {entry.type === 'separator' && <DropdownMenuSeparator />}
      </React.Fragment>
    ))}
  </DropdownMenuContent>
));
DropdownMenuContentList.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
    Icon?: React.ComponentType<{className?: string}>;
    icon?: React.ReactNode;
    endIcon?: React.ReactNode;
    EndIcon?: React.ComponentType<{className?: string}>;
    asChild?: boolean;
  }
>(
  (
    {
      className,
      inset,
      children,
      icon,
      Icon,
      asChild = false,
      endIcon,
      EndIcon,
      ...props
    },
    ref,
  ) => {
    const iconMarkup = icon ?? (Icon && <Icon className="w-4 h-4 mr-2" />);

    const endIconMarkup =
      endIcon ??
      (EndIcon && (
        <EndIcon className="ml-auto h-4 w-4 shrink-0 text-primary" />
      ));

    const itemClassName = cn(
      'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm leading-none outline-hidden transition-colors focus:bg-card focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
      inset && 'pl-8',
      className,
    );

    if (asChild) {
      return (
        <DropdownMenuPrimitive.Item
          ref={ref}
          className={itemClassName}
          asChild
          {...props}
        >
          <Slot>
            {iconMarkup}
            <Slottable>{children}</Slottable>
            {endIconMarkup}
          </Slot>
        </DropdownMenuPrimitive.Item>
      );
    }

    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={itemClassName}
        {...props}
      >
        {iconMarkup}
        {children}
        {endIconMarkup}
      </DropdownMenuPrimitive.Item>
    );
  },
);
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
    CheckboxProps?: React.ComponentPropsWithoutRef<typeof Checkbox>;
  }
>(({className, children, checked, CheckboxProps, ...props}, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <Checkbox {...CheckboxProps} checked={checked} />
      {/* <DropdownMenuPrimitive.ItemIndicator></DropdownMenuPrimitive.ItemIndicator> */}
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({className, children, ...props}, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icons.dotFilled className="h-4 w-4 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({className, inset, ...props}, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      'px-2 py-1.5 text-sm font-semibold',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({className, ...props}, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
